import { useState } from 'react';
import { FolderLock, FolderPlus, Plus, X } from 'lucide-react';
import { CaseChooser, caseChoiceIsUsable, type CaseChoice } from './CaseChooser';
import { useAuth } from '../../contexts/AuthContext';
import { canProtectLogbook } from '../../utils/logbookPermissions';
import { cn } from '../../utils/cn';

interface CaseLinkFieldProps {
  value: CaseChoice | null;
  onChange: (choice: CaseChoice | null) => void;
  /** The player the record is about, so their own cases are offered first. */
  subject?: string;
  className?: string;
}

/**
 * The case a note, warning or ban belongs to, chosen while it is being written.
 *
 * The case is the reason the record exists, so the two are decided together: attaching one
 * afterwards meant issuing the ban, finding it again in a list, and pasting an identifier between
 * two pages. It stays collapsed until it is wanted, because most records are not part of an
 * investigation and the field should cost nothing to ignore.
 *
 * Renders nothing for an operator who may not change cases: offering a field they cannot use would
 * only promise something the save is going to refuse.
 */
export const CaseLinkField = ({ value, onChange, subject, className }: CaseLinkFieldProps) => {
  const { identity } = useAuth();
  const [open, setOpen] = useState(false);
  /** What was chosen before Change was pressed, so cancelling puts it back rather than clearing it. */
  const [restore, setRestore] = useState<CaseChoice | null>(null);

  if (!canProtectLogbook(identity)) return null;

  const chosen = caseChoiceIsUsable(value) ? value : null;

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center justify-between gap-2">
        <span className="block text-xs font-medium text-glass-400">Logbook case</span>
        {!open && !chosen && <span className="text-xs text-glass-500">Optional</span>}
      </div>

      {chosen && !open ? (
        <div className="subcard flex items-center gap-2 p-2">
          {chosen.mode === 'new' ? <FolderPlus className="w-4 h-4 text-accent-300 shrink-0" /> : <FolderLock className="w-4 h-4 text-accent-300 shrink-0" />}
          <span className="min-w-0 flex-1">
            <span className="block text-sm text-white truncate">
              {chosen.mode === 'new' ? chosen.title.trim() : chosen.entry.title}
            </span>
            <span className="block text-xs text-glass-500 font-mono">
              {chosen.mode === 'new' ? 'Opens a new case' : chosen.entry.reference}
            </span>
          </span>
          <button type="button" className="glass-button-sm shrink-0" onClick={() => { setRestore(value); setOpen(true); }}>Change</button>
          <button
            type="button"
            className="btn-icon-sm shrink-0"
            title="Do not attach a case"
            aria-label="Do not attach a case"
            onClick={() => onChange(null)}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : open ? (
        <div className="subcard p-3 space-y-2">
          <CaseChooser
            value={value}
            onChange={onChange}
            subject={subject}
            autoFocus
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="glass-button-secondary"
              onClick={() => { onChange(restore); setRestore(null); setOpen(false); }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="glass-button"
              disabled={!caseChoiceIsUsable(value)}
              onClick={() => { setRestore(null); setOpen(false); }}
            >
              Use this case
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="glass-button-secondary w-full justify-center" onClick={() => { setRestore(null); setOpen(true); }}>
          <Plus className="w-4 h-4" />
          Attach a case
        </button>
      )}
    </div>
  );
};
